import { motion, useReducedMotion } from "framer-motion";
import { CalendarHeart, MapPin, Route, Clock, Car, Megaphone, Users, Shirt, CircleDot, Backpack } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { ComingSoonBadge, PlaceholderNote } from "../components/ComingSoon";
import { LiveForm, Field, TextInput, TextArea } from "../components/form";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { EVENT, IMAGES, REGISTRATION, WALK_FAQS } from "../content/site";

const Section = ({ children, className = "", id, labelledBy }) => (
  <section id={id} aria-labelledby={labelledBy} className={`scroll-mt-28 px-4 py-14 sm:px-8 sm:py-20 lg:px-16 ${className}`}>
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark sm:text-sm">{children}</p>
);

const H2 = ({ id, children }) => (
  <h2 id={id} className="font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
    {children}
  </h2>
);

const WalkHero = () => {
  const reduce = useReducedMotion();
  return (
    <header className="px-4 pb-12 pt-16 sm:px-8 sm:pb-16 sm:pt-24 lg:px-16" data-testid="walk-hero">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-7">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark sm:text-sm">
            Annual community event
          </p>
          <motion.h1
            initial={reduce ? false : { opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
            className="font-serif text-4xl font-light leading-[1.12] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          >
            Walking for Hope
          </motion.h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-body sm:text-xl">
            Join TJ's Light for a community walk centered on remembrance, suicide-prevention
            awareness, and support for people and families who may be struggling.
          </p>
        </div>
        {IMAGES.walkLogoUrl && (
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.215, 0.61, 0.355, 1] }}
            className="lg:col-span-5"
          >
            <img
              src={IMAGES.walkLogoUrl}
              alt={IMAGES.walkLogoAlt}
              className="mx-auto w-full max-w-sm object-contain"
              loading="eager"
              data-testid="walk-logo-image"
            />
          </motion.div>
        )}
      </div>
    </header>
  );
};

const DetailRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-4 rounded-xl border border-sage/10 bg-white p-5" data-testid={testId}>
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold-dark" aria-hidden="true" />
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
      {value ? (
        <p className="mt-1 text-base font-medium text-ink">{value}</p>
      ) : (
        <p className="mt-1">
          <ComingSoonBadge />
        </p>
      )}
    </div>
  </div>
);

const RegistrationBlock = () => {
  if (!REGISTRATION.open) {
    return (
      <PlaceholderNote testId="registration-closed-note">
        <strong className="font-semibold text-ink">Registration is not open yet.</strong>{" "}
        {REGISTRATION.note} When it opens, this form will collect only what is needed to
        organize the walk.
      </PlaceholderNote>
    );
  }
  return (
    <>
      <p className="mb-6 rounded-xl border border-gold/40 bg-gold-soft/50 p-4 text-sm font-medium leading-relaxed text-ink" role="note" data-testid="registration-open-note">
        {REGISTRATION.note}
      </p>
      <LiveForm
        type="registration"
        testId="walk-registration-form"
        submitLabel="Register"
        successMessage="Thank you — your registration for Walking for Hope has been received."
      >
        <div className="space-y-6">
          <Field label="Participant name" id="walk-name" required>
            <TextInput id="walk-name" required placeholder="Your full name" testId="walk-name-input" />
          </Field>
          <Field label="Email or phone" id="walk-contact" required hint="So we can reach you with event updates.">
            <TextInput id="walk-contact" required placeholder="Email address or phone number" testId="walk-contact-input" />
          </Field>
          {REGISTRATION.showTeamOption !== false && (
            <fieldset>
              <legend className="text-sm font-semibold text-ink">Registration type</legend>
              <div className="mt-3 flex flex-wrap gap-3">
                {["Individual", "Team"].map((opt) => (
                  <label key={opt} className="inline-flex min-h-[48px] cursor-pointer items-center gap-2 rounded-full border border-sage/25 px-5 py-2.5 text-sm font-medium text-ink has-[:checked]:border-sage has-[:checked]:bg-sage-soft">
                    <input type="radio" name="walk-type" value={opt.toLowerCase()} className="h-4 w-4 accent-[#05294E]" data-testid={`walk-type-${opt.toLowerCase()}-radio`} />
                    {opt}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <Field label="Number of participants" id="walk-count" hint="Including you.">
            <TextInput id="walk-count" type="number" placeholder="e.g. 4" testId="walk-count-input" />
          </Field>
          {REGISTRATION.showAccessibility !== false && (
            <Field label="Accessibility request" id="walk-accessibility" hint="Optional — anything that would make the walk easier for you to take part in.">
              <TextArea id="walk-accessibility" rows={3} placeholder="Optional" testId="walk-accessibility-textarea" />
            </Field>
          )}
          {REGISTRATION.showPhotoPermission !== false && (
            <label className="flex min-h-[48px] cursor-pointer items-start gap-3 rounded-lg border border-sage/15 bg-paper/50 p-4" data-testid="walk-photo-permission-label">
              <input type="checkbox" name="walk-photo" className="mt-1 h-5 w-5 accent-[#05294E]" data-testid="walk-photo-permission-checkbox" />
              <span className="text-sm leading-relaxed text-body">
                <span className="font-semibold text-ink">Photo permission (optional).</span>{" "}
                I'm comfortable being included in event photos. Unchecked means no.
              </span>
            </label>
          )}
        </div>
      </LiveForm>
    </>
  );
};

const MerchCard = ({ icon: Icon, title, price, children, testId }) => (
  <div className="rounded-2xl border border-sage/10 bg-white p-6 sm:p-7" data-testid={testId}>
    <div className="flex items-center justify-between gap-3">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-sage-soft text-sage">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="rounded-full bg-gold-soft px-4 py-1.5 text-sm font-bold text-ink">{price}</span>
    </div>
    <h3 className="mt-4 font-serif text-xl font-medium text-ink">{title}</h3>
    <div className="mt-2 text-base leading-relaxed text-body">{children}</div>
  </div>
);

export default function Walk() {
  const orderSteps = (EVENT.orderSteps || "").split("\n").filter(Boolean);
  const routeSteps = (EVENT.routeSteps || "").split("\n").filter(Boolean);
  const bringItems = (EVENT.whatToBring || "").split("\n").filter(Boolean);
  return (
    <>
      <WalkHero />

      <Section labelledBy="details-heading" id="details" className="bg-paper/70">
        <Reveal>
          <Eyebrow>Event details</Eyebrow>
          <H2 id="details-heading">What to know before you walk</H2>
          <p className="mt-4 max-w-3xl text-base text-body">
            Details come from the foundation's official event flyer. Any field still being
            finalized shows "Details coming soon" — nothing here is guessed.
          </p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailRow icon={CalendarHeart} label="Date and start time" value={EVENT.date && `${EVENT.date}${EVENT.startTime ? ` · ${EVENT.startTime}` : ""}`} testId="event-detail-date" />
          <DetailRow icon={MapPin} label="Location and check-in" value={EVENT.location && `${EVENT.location}${EVENT.checkInTime ? ` · check-in ${EVENT.checkInTime}` : ""}`} testId="event-detail-location" />
          <DetailRow icon={Route} label="Route" value={EVENT.route} testId="event-detail-route" />
          <DetailRow icon={Clock} label="Registration deadline" value={EVENT.registrationDeadline} testId="event-detail-deadline" />
          <DetailRow icon={Car} label="Parking and accessibility" value={EVENT.parkingAccessibility} testId="event-detail-parking" />
          <DetailRow icon={Megaphone} label="Event contact and announcements" value={EVENT.eventContact} testId="event-detail-contact" />
        </div>
        {EVENT.flyerUrl && (
          <Reveal className="mt-10">
            <figure className="mx-auto max-w-2xl">
              <img
                src={EVENT.flyerUrl}
                alt={EVENT.flyerAlt}
                className="w-full rounded-2xl border border-sage/10 shadow-md"
                loading="lazy"
                data-testid="event-flyer-image"
              />
              <figcaption className="mt-3 text-center text-sm text-muted-foreground">
                The official event flyer.{" "}
                <a
                  href={EVENT.flyerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-sage underline underline-offset-4"
                  data-testid="event-flyer-fullsize-link"
                >
                  Open the full-size flyer
                </a>
              </figcaption>
            </figure>
          </Reveal>
        )}
      </Section>

      <Section labelledBy="route-heading" id="route">
        <Reveal>
          <Eyebrow>The route</Eyebrow>
          <H2 id="route-heading">A loop through Mamou, starting and finishing at The Mou Latte</H2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          {EVENT.routeMapUrl && (
            <Reveal className="lg:col-span-7">
              <figure>
                <img
                  src={EVENT.routeMapUrl}
                  alt={EVENT.routeMapAlt}
                  className="w-full rounded-2xl border border-sage/10 shadow-md"
                  loading="lazy"
                  data-testid="route-diagram-image"
                />
                <figcaption className="mt-3 text-center text-sm text-muted-foreground">
                  Official route diagram.{" "}
                  <a
                    href={EVENT.routeMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-sage underline underline-offset-4"
                    data-testid="route-diagram-fullsize-link"
                  >
                    Open the full-size route map
                  </a>
                </figcaption>
              </figure>
            </Reveal>
          )}
          <Reveal className="lg:col-span-5" delay={0.08}>
            <div className="rounded-2xl border border-sage/10 bg-white p-6 sm:p-8" data-testid="route-steps-card">
              <h3 className="font-serif text-xl font-medium text-ink">Turn by turn</h3>
              <ol className="mt-4 list-decimal space-y-3 pl-5 text-base leading-relaxed text-body">
                {routeSteps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
              {EVENT.routeNote && (
                <p className="mt-5 border-t border-sage/10 pt-4 text-xs leading-relaxed text-muted-foreground">
                  {EVENT.routeNote}
                </p>
              )}
            </div>
          </Reveal>
        </div>
        {bringItems.length > 0 && (
          <Reveal className="mt-10">
            <div className="rounded-2xl border border-sage/10 bg-white p-6 sm:p-8" data-testid="what-to-bring-card">
              <div className="flex items-start gap-4">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sage-soft text-sage">
                  <Backpack className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-serif text-xl font-medium text-ink">What to bring</h3>
                  <ul className="mt-4 list-disc space-y-2.5 pl-5 text-base leading-relaxed text-body">
                    {bringItems.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  {EVENT.bringNote && (
                    <p className="mt-5 rounded-xl bg-gold-soft/50 p-4 text-sm font-medium leading-relaxed text-ink">
                      {EVENT.bringNote}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Reveal>
        )}
      </Section>

      <Section labelledBy="merch-heading" id="merchandise" className="bg-paper/70">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <Eyebrow>Event merchandise</Eyebrow>
            <H2 id="merch-heading">Shirts and buttons — available now</H2>
            <p className="mt-4 max-w-3xl text-base text-body">{EVENT.merchIntro}</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Reveal>
              <MerchCard icon={Shirt} title="Walk shirts" price={EVENT.shirtPrice} testId="merch-shirt-card">
                <p>Official Walking for Hope shirts.</p>
              </MerchCard>
            </Reveal>
            <Reveal delay={0.08}>
              <MerchCard icon={CircleDot} title="Buttons" price={EVENT.buttonPrice} testId="merch-button-card">
                <p>{EVENT.buttonNote}</p>
              </MerchCard>
            </Reveal>
          </div>
          <Reveal className="mt-8">
            <div className="grid grid-cols-1 gap-6 rounded-2xl border border-sage/10 bg-white p-6 sm:grid-cols-2 sm:p-8" data-testid="merch-order-block">
              <div>
                <h3 className="font-serif text-xl font-medium text-ink">How to order</h3>
                <ol className="mt-4 list-decimal space-y-2.5 pl-5 text-base leading-relaxed text-body">
                  {orderSteps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
              <div>
                <h3 className="font-serif text-xl font-medium text-ink">Payment (merchandise only)</h3>
                {EVENT.zelleQrUrl && (
                  <div className="mt-4">
                    <a
                      href={EVENT.cashAppUrl || EVENT.zelleQrUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block rounded-xl transition-transform duration-200 hover:scale-[1.02]"
                      aria-label="Pay with Cash App — opens cash.app in a new tab"
                      data-testid="merch-qr-link"
                    >
                      <img
                        src={EVENT.zelleQrUrl}
                        alt={EVENT.zelleQrAlt}
                        className="w-full max-w-sm rounded-xl border border-sage/10 bg-white"
                        loading="lazy"
                        data-testid="merch-zelle-qr"
                      />
                    </a>
                    {EVENT.cashAppUrl && (
                      <a
                        href={EVENT.cashAppUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-sage underline underline-offset-4"
                        data-testid="merch-cashapp-link"
                      >
                        On your phone? Tap to pay with Cash App
                      </a>
                    )}
                  </div>
                )}
                <p className="mt-3 text-sm leading-relaxed text-body">{EVENT.paymentNote}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section labelledBy="register-heading" id="register">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <Eyebrow>Registration</Eyebrow>
            <H2 id="register-heading">Walk with us</H2>
            <p className="mt-4 text-base text-body">
              Registration collects only what is needed to organize the walk. Submissions
              are stored securely and visible only to the site owner.
            </p>
          </Reveal>
          <Reveal className="mt-8">
            <div className="rounded-2xl border border-sage/10 bg-white p-6 sm:p-8">
              {EVENT.regFormUrl && (
                <div className="mb-8 rounded-xl border border-gold/40 bg-gold-soft/40 p-5 sm:p-6" data-testid="paper-form-card">
                  <h3 className="font-serif text-xl font-medium text-ink">Prefer paper?</h3>
                  <p className="mt-2 text-base leading-relaxed text-body">{EVENT.paperNote}</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {EVENT.regFormPdfUrl && (
                      <a
                        href={EVENT.regFormPdfUrl}
                        download
                        className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-sage px-6 py-3 text-sm font-semibold text-cream transition-colors hover:bg-sage-light"
                        data-testid="download-form-pdf-button"
                      >
                        Download form (PDF)
                      </a>
                    )}
                    <a
                      href={EVENT.regFormUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-[48px] items-center gap-2 rounded-full border border-sage/30 px-6 py-3 text-sm font-semibold text-sage transition-colors hover:bg-sage-soft"
                      data-testid="download-form-image-button"
                    >
                      Open form image
                    </a>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    The form includes the participant information, shirt and button ordering, and
                    waiver with a signature line.
                  </p>
                </div>
              )}
              <RegistrationBlock />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section labelledBy="teams-heading" className="bg-paper/70">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <Eyebrow>Teams and fundraising</Eyebrow>
            <H2 id="teams-heading">Walk together, give together</H2>
            <div className="mt-6">
              <PlaceholderNote testId="teams-placeholder">
                <strong className="font-semibold text-ink">Coming soon.</strong>{" "}
                Team registration and fundraising details will be added here once they are
                confirmed by the foundation.
              </PlaceholderNote>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section labelledBy="sponsors-heading">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <Eyebrow>Sponsors and partners</Eyebrow>
            <H2 id="sponsors-heading">With thanks to our community</H2>
            <div className="mt-6">
              <PlaceholderNote testId="sponsors-placeholder">
                <strong className="font-semibold text-ink">Sponsor display — coming soon.</strong>{" "}
                The businesses and community partners supporting Walking for Hope will be
                recognized here. Interested in sponsoring?{" "}
                <a href="/get-involved#sponsor" className="font-semibold text-sage underline underline-offset-4">
                  Start the conversation
                </a>
                .
              </PlaceholderNote>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section labelledBy="faq-heading" className="bg-paper/70">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <Eyebrow>Questions</Eyebrow>
            <H2 id="faq-heading">Event FAQs</H2>
          </Reveal>
          <Reveal className="mt-8">
            <Accordion type="single" collapsible className="rounded-2xl border border-sage/10 bg-white px-6" data-testid="walk-faq-accordion">
              {WALK_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold text-ink" data-testid={`walk-faq-trigger-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-body">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </Section>

      <Section labelledBy="walk-closing-heading">
        <Reveal className="mx-auto max-w-3xl text-center">
          <Users className="mx-auto h-8 w-8 text-gold-dark" aria-hidden="true" />
          <h2 id="walk-closing-heading" className="mt-4 font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl">
            Every step says: you are not alone.
          </h2>
          <p className="mt-4 text-lg text-body">
            Walk in remembrance. Walk for hope. Together we can shine a light on hope.
          </p>
        </Reveal>
      </Section>
    </>
  );
}
